import { StrictMode } from "react";
import { createRoot } from "react-dom/client";
import { MsalProvider } from "@azure/msal-react";
import { EventType, type EventMessage, type AuthenticationResult } from "@azure/msal-browser";
import { msalInstance } from "./auth/msalConfig";
import App from "./App";
// redesign-panel-hubspot-mailgun, PR 17 (task 17.4): `legacy.css` was a
// pure strangler-pattern forwarder (`@import "./base.css";`, design.md §9)
// with zero rules of its own — deleted here, `base.css` is imported
// directly. `base.css` itself still provides the baseline semantic-element
// styling (table/form/dl/button/section[aria-label]/[role=status]/etc.)
// every v2 screen layers its OWN scoped classes on top of (design.md §9's
// own convention); only the pre-AppShell nav/user-menu rules it ALSO used
// to carry were dead (removed in this same slice, task 17.2) — the file
// was never fully retired, so it stays a real import, not a relic.
// Self-hosted @font-face declarations (Plus Jakarta Sans + Inter, latin
// subset) — imported first so the faces are registered before any other
// stylesheet references --font-ui/--font-display (tokens.css, imported by
// every file below, only aliases the family names).
import "./styles/fonts.css";
import "./styles/base.css";
import "./styles/reset.css";
import "./styles/primitives.css";
import "./styles/combobox.css";
import "./styles/modal-v2.css";
import "./styles/shell.css";
import "./styles/features/overview.css";
// Campaigns (PR8, task 8.2/8.4): list/detail/tabs, plus the shared
// DataTable/RecordList/Toolbar/Pagination/DetailGroup primitive layout.
import "./styles/features/campaigns.css";
import "./styles/features/contacts.css";
// Segments (PR13, task 13.2/13.5): compact read-only list + directly
// addressable detail route, reusing the shared data-table/record-list toggle.
import "./styles/features/segments.css";
// Telemetry (PR10, task 10.6): shared aggregate evidence matrix, consumed by
// both `/telemetria` and the campaign-scoped telemetry tab.
import "./styles/features/telemetry.css";
// Operational health (PR11, task 11.7): read-only "Estado operativo" screen at /configuracion.
import "./styles/features/operational-health.css";
// Templates (PR14 slice C, task 14.2/14.5): list/detail, the real
// DataTable/RecordList responsive toggle Slice A deferred, and the routed
// detail's preview/metadata layout.
import "./styles/features/templates.css";

// Set an active account automatically after a successful login/acquire, so
// useMsal()/RequireRole always see the signed-in principal without every
// screen having to call setActiveAccount itself.
msalInstance.addEventCallback((event: EventMessage) => {
  if (
    (event.eventType === EventType.LOGIN_SUCCESS || event.eventType === EventType.ACQUIRE_TOKEN_SUCCESS) &&
    event.payload
  ) {
    const account = (event.payload as AuthenticationResult).account;
    if (account) {
      msalInstance.setActiveAccount(account);
    }
  }
});

async function bootstrap() {
  await msalInstance.initialize();

  // Do NOT call handleRedirectPromise() here too: <MsalProvider> already calls it
  // internally on mount. Calling it twice consumes the redirect's temporary cache
  // entry on the first call, so the second (MsalProvider's) finds nothing and throws
  // BrowserAuthError: no_token_request_cache_error. Let MsalProvider own that lifecycle.

  const existingAccount = msalInstance.getAllAccounts()[0];
  if (existingAccount) {
    msalInstance.setActiveAccount(existingAccount);
  }

  createRoot(document.getElementById("root")!).render(
    <StrictMode>
      <MsalProvider instance={msalInstance}>
        <App />
      </MsalProvider>
    </StrictMode>,
  );
}

void bootstrap();
